import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Badge from '../../components/common/Badge/Badge';
import Button from '../../components/common/Button/Button';
import ClearFiltersButton from '../../components/common/ClearFiltersButton/ClearFiltersButton';
import ConfirmModal from '../../components/common/ConfirmModal/ConfirmModal';
import DataTable from '../../components/common/DataTable/DataTable';
import DatePresetFilter from '../../components/common/DatePresetFilter/DatePresetFilter';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import FilterBar from '../../components/common/FilterBar/FilterBar';
import FormTextarea from '../../components/common/FormTextarea/FormTextarea';
import FilterChipsBar, { type FilterChipEntry } from '../../components/common/FilterChipsBar/FilterChipsBar';
import FilterGroup from '../../components/common/FilterGroup/FilterGroup';
import ListingBottomRow from '../../components/common/ListingBottomRow/ListingBottomRow';
import Loading from '../../components/common/Loading/Loading';
import MultiSelectFilter from '../../components/common/MultiSelectFilter/MultiSelectFilter';
import RecordCount from '../../components/common/RecordCount/RecordCount';
import SearchableSelect from '../../components/common/SearchableSelect/SearchableSelect';
import SearchFilterBar from '../../components/common/SearchFilterBar/SearchFilterBar';
import ViewLinkButton from '../../components/common/ViewLinkButton/ViewLinkButton';
import InsightBanner from '../archived/dashboards/CommercialDashboard/components/InsightBanner';
import {
  PAYMENTS_RECEIVED_PERIOD_PRESETS,
  detectDatePreset,
  formatDatePresetShort,
} from '../../constants/datePresets';
import {
  DELINQUENCY_FOLLOW_UP_STAGE_OPTIONS,
  isDelinquencyFollowUpStage,
  type DelinquencyFollowUpStage,
} from '../../constants/delinquencyFollowUpStage';
import { useDashboardDelinquents } from '../../hooks/useDashboardDelinquents';
import { queryFilter, useQueryFilters } from '../../hooks/useQueryFilters';
import { DashboardService } from '../../services/DashboardService';
import { PAYMENT_SYSTEM_LABELS, type PaymentSystem } from '../../types/payment';
import { getDelinquentPaidRangeError } from '../../utils/dashboardSupportSnapshot';
import { formatOptionalDate } from './components/SupportLists';
import dashboardStyles from './Dashboard.module.css';
import styles from './SupportDashboard.module.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50] as const;
const GATEWAY_FILTER_VALUES = ['asaas', 'stripe', 'paypal', 'pix'] as const;
type DelinquentGateway = (typeof GATEWAY_FILTER_VALUES)[number];

const HEADERS = ['Aluna', 'Gateway', 'Último pagamento', 'Dias', 'Estágio', 'Notas'];
const GRID_TEMPLATE = 'minmax(160px, 1.3fr) minmax(110px, 0.8fr) 150px 96px minmax(180px, 1.1fr) minmax(180px, 1.3fr)';
const NOTE_MAX_LENGTH = 2000;
const NOTE_PREVIEW_LENGTH = 90;

interface PendingStageChange {
  studentId: number;
  studentName: string;
  currentStage: string | null;
  nextStage: DelinquencyFollowUpStage;
}

interface NoteTarget {
  studentId: number;
  studentName: string;
  notes: string;
}

interface NoteEditorState extends NoteTarget {
  mode: 'add' | 'edit';
  draft: string;
}

const notePreview = (notes: string): string => {
  const trimmed = notes.trim();
  if (trimmed.length <= NOTE_PREVIEW_LENGTH) {
    return trimmed;
  }

  return `${trimmed.slice(0, NOTE_PREVIEW_LENGTH).trimEnd()}…`;
};

const gatewayLabel = (systems: string[] | undefined): string => {
  if (!systems || systems.length === 0) {
    return '—';
  }

  return systems.map((value) => {
    if (Object.prototype.hasOwnProperty.call(PAYMENT_SYSTEM_LABELS, value)) {
      return PAYMENT_SYSTEM_LABELS[value as PaymentSystem];
    }

    return value;
  }).join(', ');
};

const DELINQUENT_LIST_SCHEMA = {
  searchName: queryFilter.string(''),
  paymentGateway: queryFilter.array<DelinquentGateway>([], { allowed: GATEWAY_FILTER_VALUES }),
  paidStart: queryFilter.string('', { param: 'pagamentoDe', trim: true }),
  paidEnd: queryFilter.string('', { param: 'pagamentoAte', trim: true }),
  page: queryFilter.integer(1, { min: 1 }),
  pageSize: queryFilter.integer(20, { allowed: PAGE_SIZE_OPTIONS }),
};

const isDelinquentGateway = (value: string): value is DelinquentGateway => (
  (GATEWAY_FILTER_VALUES as readonly string[]).includes(value)
);

const paidRangeLabel = (start: string, end: string): string => {
  const preset = detectDatePreset(start, end, PAYMENTS_RECEIVED_PERIOD_PRESETS);
  const option = PAYMENTS_RECEIVED_PERIOD_PRESETS.find((item) => item.value === preset);
  if (option && preset !== 'custom') {
    return option.label;
  }

  return `${formatDatePresetShort(start)} – ${formatDatePresetShort(end)}`;
};

const SupportDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { filters, setFilter, setFilters, resetFilters } = useQueryFilters(
    DELINQUENT_LIST_SCHEMA,
    { pageKey: 'page' },
  );
  const paidRangeError = getDelinquentPaidRangeError(filters.paidStart, filters.paidEnd);
  const listQuery = useMemo(() => ({
    page: filters.page,
    pageSize: filters.pageSize,
    searchName: filters.searchName,
    paymentGateway: filters.paymentGateway,
    paidStart: filters.paidStart,
    paidEnd: filters.paidEnd,
    enabled: paidRangeError == null,
  }), [
    filters.page,
    filters.pageSize,
    filters.paidEnd,
    filters.paidStart,
    filters.paymentGateway,
    filters.searchName,
    paidRangeError,
  ]);
  const { rows, pagination, loading, error, patchDelinquent } = useDashboardDelinquents(listQuery);
  const [pendingStageChange, setPendingStageChange] = useState<PendingStageChange | null>(null);
  const [pendingNoteEdit, setPendingNoteEdit] = useState<NoteTarget | null>(null);
  const [noteEditor, setNoteEditor] = useState<NoteEditorState | null>(null);
  const [noteView, setNoteView] = useState<NoteTarget | null>(null);
  const [savingStageStudentId, setSavingStageStudentId] = useState<number | null>(null);
  const [savingNote, setSavingNote] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const hasPaidFilter = Boolean(filters.paidStart && filters.paidEnd);
  const hasActiveFilters = Boolean(filters.searchName.trim())
    || filters.paymentGateway.length > 0
    || hasPaidFilter;
  const gatewayOptions = useMemo(() => GATEWAY_FILTER_VALUES.map((value) => ({
    value,
    label: PAYMENT_SYSTEM_LABELS[value],
  })), []);
  const chips: FilterChipEntry[] = useMemo(() => {
    const entries: FilterChipEntry[] = filters.paymentGateway.map((value) => ({
      filterKey: 'paymentGateway',
      value,
      label: PAYMENT_SYSTEM_LABELS[value],
      onRemove: () => setFilter(
        'paymentGateway',
        filters.paymentGateway.filter((item) => item !== value),
      ),
    }));

    if (hasPaidFilter) {
      entries.push({
        filterKey: 'paidDate',
        value: `${filters.paidStart}:${filters.paidEnd}`,
        label: `Pagamento: ${paidRangeLabel(filters.paidStart, filters.paidEnd)}`,
        onRemove: () => setFilters({ paidStart: '', paidEnd: '' }),
      });
    }

    return entries;
  }, [
    filters.paidEnd,
    filters.paidStart,
    filters.paymentGateway,
    hasPaidFilter,
    setFilter,
    setFilters,
  ]);

  useEffect(() => {
    if (loading || paidRangeError || pagination.totalPages < 1) {
      return;
    }

    if (filters.page > pagination.totalPages) {
      setFilter('page', pagination.totalPages, { resetPage: false });
    }
  }, [filters.page, loading, paidRangeError, pagination.totalPages, setFilter]);

  const handleStageSelect = (studentId: number, studentName: string, currentStage: string | null, nextStage: string) => {
    if (!isDelinquencyFollowUpStage(nextStage) || nextStage === currentStage) {
      return;
    }

    setPendingStageChange({
      studentId,
      studentName,
      currentStage,
      nextStage,
    });
  };

  const handleStageConfirm = async () => {
    if (!pendingStageChange || savingStageStudentId != null) {
      return;
    }

    const change = pendingStageChange;
    setSavingStageStudentId(change.studentId);
    setSaveError(null);

    try {
      const saved = await DashboardService.updateDelinquencyFollowUp({
        studentId: change.studentId,
        action: 'update-stage',
        stage: change.nextStage,
      });
      patchDelinquent(change.studentId, { followUpStage: saved.stage });
      setPendingStageChange(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Não foi possível salvar o estágio.');
    } finally {
      setSavingStageStudentId(null);
    }
  };

  const handleNoteEditConfirm = () => {
    if (!pendingNoteEdit) {
      return;
    }

    setNoteEditor({
      ...pendingNoteEdit,
      mode: 'edit',
      draft: pendingNoteEdit.notes,
    });
    setPendingNoteEdit(null);
  };

  const handleNoteSave = async () => {
    if (!noteEditor || savingNote) {
      return;
    }

    const nextNotes = noteEditor.draft.trim();
    if (nextNotes === noteEditor.notes.trim()) {
      setNoteEditor(null);
      return;
    }

    setSavingNote(true);
    setSaveError(null);

    try {
      const saved = await DashboardService.updateDelinquencyFollowUp({
        studentId: noteEditor.studentId,
        action: 'update-notes',
        notes: nextNotes,
      });
      patchDelinquent(noteEditor.studentId, { followUpNotes: saved.notes });
      setNoteEditor(null);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Não foi possível salvar a observação.');
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.section}>
        <InsightBanner tone="warning" title="Acompanhar inadimplente">
          Alunas <b>ativas</b> com assinatura em aberto e sem pagamento após o período do plano
          (mensal: mais de 40 dias; bimestral/trimestral/semestral/anual: fim do ciclo + 10 dias),
          considerando a data de <b>pagamento</b>. Pausa e crédito de consultoria são descontados
          desses dias. Quem ainda está coberta pelo período pago não entra nesta lista. Alunas
          inativas não aparecem aqui.
        </InsightBanner>

        <section className={styles.list}>
          <FilterBar gap="sm" compact pushFirstChild className={dashboardStyles.filtersRow}>
            <RecordCount
              label="Inadimplentes:"
              count={paidRangeError ? 0 : pagination.totalRecords}
            />
            <FilterGroup>
              <SearchFilterBar
                searchValue={filters.searchName}
                onSearchChange={(value) => setFilter('searchName', value)}
                searchPlaceholder="Nome ou e-mail (aluna ou pagador)"
                loading={loading}
              />
            </FilterGroup>
            <FilterGroup>
              <DatePresetFilter
                dateStart={filters.paidStart}
                dateEnd={filters.paidEnd}
                onChange={(start, end) => setFilters({ paidStart: start, paidEnd: end })}
                placeholder="Data de pagamento"
                presetOptions={PAYMENTS_RECEIVED_PERIOD_PRESETS}
                allowEmpty
              />
            </FilterGroup>
            <FilterGroup>
              <MultiSelectFilter
                values={filters.paymentGateway}
                onChange={(values) => setFilter('paymentGateway', values.filter(isDelinquentGateway))}
                options={gatewayOptions}
                placeholder="Gateway"
                clearButtonLabel="Limpar gateway"
                hideChips
              />
            </FilterGroup>
            {hasActiveFilters && (
              <ClearFiltersButton onClick={() => resetFilters()} />
            )}
          </FilterBar>
          <FilterChipsBar chips={chips} compact />
          {saveError && <p className={styles['save-error']}>{saveError}</p>}
          {loading && !paidRangeError && <Loading />}
          {!loading && paidRangeError && (
            <EmptyState message={paidRangeError} showImage={false} />
          )}
          {!loading && !paidRangeError && error && (
            <EmptyState message={`Erro ao carregar inadimplentes: ${error}`} showImage={false} />
          )}
          {!loading && !paidRangeError && !error && rows.length === 0 && (
            <EmptyState
              message={hasActiveFilters
                ? 'Nenhuma aluna encontrada com os filtros selecionados.'
                : 'Nenhuma aluna inadimplente neste critério.'}
              showImage={false}
            />
          )}
          {!loading && !paidRangeError && !error && rows.length > 0 && (
            <>
              <DataTable
                headers={HEADERS}
                gridTemplate={GRID_TEMPLATE}
                lastColumnIsActions={false}
                borderedRows
                boldHeaders
              >
                {rows.map((row) => {
                  const currentStage = row.followUpStage && isDelinquencyFollowUpStage(row.followUpStage)
                    ? row.followUpStage
                    : null;
                  const notes = (row.followUpNotes ?? '').trim();
                  const hasNotes = notes.length > 0;

                  return (
                    <DataTable.Row key={`${row.studentId}-${row.lastPaidAt ?? 'none'}`}>
                      <div>
                        <button
                          type="button"
                          className={styles['name-link']}
                          onClick={() => navigate(`/alunas/${row.studentId}/editar`)}
                        >
                          {row.studentName || '—'}
                        </button>
                      </div>
                      <div>{gatewayLabel(row.paymentSystems)}</div>
                      <div className={styles.nowrap}>
                        {row.lastPaidAt ? formatOptionalDate(row.lastPaidAt) : 'Sem pagamento registrado'}
                      </div>
                      <div>
                        <Badge variant="danger">
                          {row.daysOverdue != null ? `${row.daysOverdue} dias` : '> 40 dias'}
                        </Badge>
                      </div>
                      <div className={styles['stage-select']}>
                        <SearchableSelect
                          value={currentStage ?? ''}
                          onChange={(value) => {
                            handleStageSelect(row.studentId, row.studentName, currentStage, value);
                          }}
                          options={DELINQUENCY_FOLLOW_UP_STAGE_OPTIONS}
                          placeholder="Selecione"
                          allowEmpty={false}
                          className={styles['stage-select-trigger']}
                          disabled={savingStageStudentId === row.studentId}
                        />
                      </div>
                      <div className={styles['note-cell']}>
                        {hasNotes && (
                          <p className={styles['note-preview']}>{notePreview(notes)}</p>
                        )}
                        <div className={styles['note-actions']}>
                          {hasNotes && (
                            <Button
                              type="button"
                              variant="link"
                              size="xs"
                              onClick={() => setNoteView({
                                studentId: row.studentId,
                                studentName: row.studentName,
                                notes,
                              })}
                            >
                              Ver mais
                            </Button>
                          )}
                          {hasNotes ? (
                            <Button
                              type="button"
                              variant="ghost"
                              size="xs"
                              onClick={() => setPendingNoteEdit({
                                studentId: row.studentId,
                                studentName: row.studentName,
                                notes,
                              })}
                            >
                              Editar
                            </Button>
                          ) : (
                            <ViewLinkButton
                              label="Registrar"
                              icon="add_notes"
                              aria-label={`Registrar observação de ${row.studentName || 'aluna'}`}
                              onClick={() => setNoteEditor({
                                studentId: row.studentId,
                                studentName: row.studentName,
                                notes,
                                mode: 'add',
                                draft: '',
                              })}
                            />
                          )}
                        </div>
                      </div>
                    </DataTable.Row>
                  );
                })}
              </DataTable>
              <ListingBottomRow
                pageSize={filters.pageSize}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onPageSizeChange={(event) => {
                  const value = Number.parseInt(event.target.value, 10);
                  if (PAGE_SIZE_OPTIONS.some((option) => option === value)) {
                    setFilter('pageSize', value);
                  }
                }}
                pagination={pagination}
                loading={loading}
                onPageChange={(nextPage) => setFilter('page', nextPage, { resetPage: false })}
              />
            </>
          )}
        </section>
      </div>

      <ConfirmModal
        open={Boolean(pendingStageChange)}
        title="Alterar estágio"
        confirmLabel="Alterar"
        cancelLabel="Cancelar"
        variant="info"
        icon="swap_horiz"
        loading={savingStageStudentId != null}
        onCancel={() => {
          if (savingStageStudentId != null) {
            return;
          }
          setPendingStageChange(null);
        }}
        onConfirm={() => {
          void handleStageConfirm();
        }}
      >
        {pendingStageChange && (
          <p className={styles['confirm-message']}>
            Deseja alterar o estágio de <strong>{pendingStageChange.studentName}</strong>
            {' '}de{' '}
            <strong>{pendingStageChange.currentStage ?? 'sem estágio'}</strong>
            {' '}para{' '}
            <strong>{pendingStageChange.nextStage}</strong>?
          </p>
        )}
      </ConfirmModal>

      <ConfirmModal
        open={Boolean(pendingNoteEdit)}
        title="Editar observação"
        confirmLabel="Editar"
        cancelLabel="Cancelar"
        variant="info"
        icon="edit"
        onCancel={() => setPendingNoteEdit(null)}
        onConfirm={handleNoteEditConfirm}
      >
        {pendingNoteEdit && (
          <p className={styles['confirm-message']}>
            Deseja mesmo editar a observação de <strong>{pendingNoteEdit.studentName}</strong>?
          </p>
        )}
      </ConfirmModal>

      <ConfirmModal
        open={Boolean(noteEditor)}
        title={noteEditor?.mode === 'edit' ? 'Editar observação' : 'Adicionar notas'}
        confirmLabel="Salvar"
        cancelLabel="Cancelar"
        variant="info"
        icon="edit_note"
        alignment="left"
        width={480}
        loading={savingNote}
        onCancel={() => {
          if (savingNote) {
            return;
          }
          setNoteEditor(null);
        }}
        onConfirm={() => {
          void handleNoteSave();
        }}
      >
        {noteEditor && (
          <FormTextarea
            id="student-notes-modal-new-note"
            value={noteEditor.draft}
            maxLength={NOTE_MAX_LENGTH}
            rows={2}
            minRows={2}
            placeholder="Escreva aqui a observação que deseja enviar."
            aria-label={`Observação de ${noteEditor.studentName || 'aluna'}`}
            disabled={savingNote}
            onChange={(event) => {
              const value = event.target.value;
              setNoteEditor((current) => (current ? { ...current, draft: value } : current));
            }}
          />
        )}
      </ConfirmModal>

      <ConfirmModal
        open={Boolean(noteView)}
        title={noteView ? `Observação — ${noteView.studentName}` : 'Observação'}
        confirmLabel="Fechar"
        hideCancel
        variant="info"
        icon="notes"
        alignment="left"
        width={480}
        onCancel={() => setNoteView(null)}
        onConfirm={() => setNoteView(null)}
      >
        {noteView && <p className={styles['note-full']}>{noteView.notes}</p>}
      </ConfirmModal>
    </div>
  );
};

export default SupportDashboard;
